import React, { useEffect, useState } from 'react';
import { Box, Code, EmptyContent, Flex, Text } from '@/components/ui';
import { PreviewRendererProps, PreviewState } from '../types';
import { IconAlertCircle, IconFileText } from '@tabler/icons-react';
import { api } from '@/services/http';

interface TextPreviewRendererProps extends PreviewRendererProps {
  maxPreviewSize?: number; // Maximum file size to preview in bytes
}

const TextPreviewRenderer: React.FC<TextPreviewRendererProps> = ({
  apiEndpoint,
  filename,
  fileSize,
  maxPreviewSize = 1024 * 1024, // 1MB default limit
}) => {
  const [state, setState] = useState<PreviewState>({
    loading: true,
    error: null,
    data: null,
  });

  useEffect(() => {
    let isCancelled = false;

    const loadTextContent = async () => {
      setState({ loading: true, error: null, data: null });

      // Check file size limit
      if (fileSize && fileSize > maxPreviewSize) {
        setState({
          loading: false,
          error: 'file-too-large',
          data: { fileSize, maxPreviewSize },
        });
        return;
      }

      try {
        // Use API endpoint for download with authentication
        if (!apiEndpoint) {
          throw new Error('API endpoint is required for file download');
        }

        const response = await api.get(apiEndpoint, {
          responseType: 'text',
          headers: {
            Accept: 'text/plain, text/csv, */*',
          },
        });

        const textContent = response.data;

        if (isCancelled) return;

        setState({
          loading: false,
          error: null,
          data: { content: textContent },
        });
      } catch (error: unknown) {
        if (isCancelled) return;

        const errorMessage = error instanceof Error ? error.message : 'Unknown error';

        let errorDetails: string | undefined;
        if (
          error &&
          typeof error === 'object' &&
          'response' in error &&
          (error as { response?: unknown }).response &&
          typeof (error as { response?: unknown }).response === 'object'
        ) {
          const response = (error as { response?: unknown }).response as {
            status?: unknown;
            statusText?: unknown;
          };
          if (typeof response.status === 'number') {
            const statusText =
              typeof response.statusText === 'string' ? response.statusText : '';
            errorDetails = `HTTP ${response.status}: ${statusText}`.trim();
          }
        }

        setState({
          loading: false,
          error: 'network-error',
          data: {
            message: errorMessage,
            details: errorDetails,
          },
        });
      }
    };

    loadTextContent();

    return () => {
      isCancelled = true;
    };
  }, [apiEndpoint, fileSize, maxPreviewSize]);

  // Loading state
  if (state.loading) {
    return (
      <EmptyContent
        size="medium"
        icon={<IconFileText />}
        title="Loading Text Preview"
        description="Please wait while we load the file content..."
      />
    );
  }

  // Error states
  if (state.error) {
    if (state.error === 'file-too-large') {
      const size =
        state.data &&
        typeof state.data === 'object' &&
        'fileSize' in state.data &&
        typeof (state.data as { fileSize?: unknown }).fileSize === 'number'
          ? (state.data as { fileSize: number }).fileSize
          : 0;
      const maxSize =
        state.data &&
        typeof state.data === 'object' &&
        'maxPreviewSize' in state.data &&
        typeof (state.data as { maxPreviewSize?: unknown }).maxPreviewSize ===
          'number'
          ? (state.data as { maxPreviewSize: number }).maxPreviewSize
          : 0;
      return (
        <EmptyContent
          size="medium"
          state="warning"
          icon={<IconAlertCircle />}
          title="File Too Large for Preview"
          description={`This file (${Math.round(size / 1024)} KB) is too large to preview. Maximum preview size is ${Math.round(maxSize / 1024)} KB. Please download the file to view its content.`}
        />
      );
    }

    const errorMessages = {
      'download-failed':
        'Failed to download the file. Please try again or download the file directly.',
      'parse-error':
        'Unable to read the file content. The file may be corrupted or in binary format.',
      'network-error':
        'Network error occurred while loading the file. Please check your connection.',
      'unsupported-format': 'This file format is not supported for preview.',
      'unknown-error':
        'An unexpected error occurred while loading the preview.',
    };

    return (
      <EmptyContent
        size="medium"
        state="error"
        // withIconBox
        icon={<IconAlertCircle />}
        title="Preview Not Available"
        description={
          errorMessages[state.error as keyof typeof errorMessages] ||
          errorMessages['unknown-error']
        }
      />
    );
  }

  const textContent =
    state.data &&
    typeof state.data === 'object' &&
    'content' in state.data &&
    typeof (state.data as { content?: unknown }).content === 'string'
      ? (state.data as { content: string }).content
      : '';

  if (!textContent.trim()) {
    return (
      <EmptyContent
        size="medium"
        icon={<IconFileText />}
        title="Empty File"
        description="This file appears to be empty or contains only whitespace."
      />
    );
  }

  // Determine if content should be displayed as code based on file extension
  const extension = filename.toLowerCase().split('.').pop() || '';
  const codeExtensions = [
    'js',
    'ts',
    'tsx',
    'jsx',
    'json',
    'xml',
    'html',
    'css',
    'scss',
    'sass',
    'py',
    'rb',
    'php',
    'java',
    'c',
    'cpp',
    'cs',
    'go',
    'rs',
    'sql',
  ];
  const isCode = codeExtensions.includes(extension);

  return (
    <Flex direction="column" gap="3">
      {/* File info */}
      <Flex justify="between" align="center" wrap="wrap" gap="2">
        <Text size="2" color="gray">
          {Math.round((textContent.length / 1024) * 100) / 100} KB •{' '}
          {textContent.split('\n').length} lines
        </Text>
        <Text size="2" color="gray">
          {isCode ? 'Code' : 'Text'} file
        </Text>
      </Flex>

      {/* Content */}
      <Box
        style={{
          maxHeight: '60vh',
          overflow: 'auto',
          border: '1px solid var(--gray-6)',
          borderRadius: '8px',
          padding: '12px',
          backgroundColor: 'var(--gray-1)',
        }}
      >
        {isCode ? (
          <Code
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: '13px',
              lineHeight: '1.5',
              fontFamily:
                'ui-monospace, SFMono-Regular, "SF Mono", Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            }}
          >
            {textContent}
          </Code>
        ) : (
          <Text
            size="2"
            style={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: '1.6',
            }}
          >
            {textContent}
          </Text>
        )}
      </Box>
    </Flex>
  );
};

export default TextPreviewRenderer;
